<!--
// app/pages/players/[player].vue
-->
<script setup lang="ts">
import type { Player, PlayerRanking, PlayerSeasons, SeasonPlayerInfo } from "~/src/player/domain/Player";
import type { Match } from "~/src/match/domain/models/Match";
import type { Season, SeasonStatsPartner } from "~/src/season-cluster/domain/models/01.Season";
import type { Team } from "~/src/team/domain/models/Team";
import { usePlayers } from "~/app/composables/players";
import { useSeasonFilter } from "~/app/composables/season/seasonFilter";
import { useTeams } from "~/app/composables/teams";
import { useSeasons } from "~/app/composables/season/seasons";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import { setAliasUrl, slugify } from "~/src/Shared/utils";
import { teamArrToObjById } from "~/src/team/domain/services/teamService";
import { useWebContext } from "~/app/composables/webContext";
import { useTeamAndPlayerStore } from "~/app/stores/teamAndPlayerStore";
import matchService from "~/src/match/domain/services/matchService";
import {
  $SEASON_ROUTE_QUERY_NAME,
  $SEASONS_WITHOUT_DATA,
} from "~/src/season-cluster/infrastructure/ui-services/seasonUiService";
import BaseNuxtLink from "~/app/components/base/baseNuxtLink.vue";
import BaseBackdropLoading from "~/app/components/base/BaseBackdropLoading.vue";
import TeamSeasonPlayersSlider from "~/app/components/pages/players-player/TeamSeasonPlayersSlider.vue";
import FeaturedPlayerStats from "~/app/components/pages/players-player/FeaturedPlayerStats.vue";
import PlayerStats from "~/app/components/pages/players-player/PlayerStats.vue";
import PlayerSeasonMatches from "~/app/components/pages/players-player/PlayerSeasonMatches.vue";
import PlayerSeasonsFilter from "~/app/components/pages/players-player/PlayerSeasonsFilter.vue";
import PrevIcon from "~/app/components/Shared/icons/PrevIcon.vue";
import HeroPlayerPage from "~/app/components/pages/players-player/HeroPlayerPage.vue";
import RegionUiService, {
  $REGION_ROUTE_QUERY_NAME,
} from "~/src/region/infrastructure/ui-services/regionUiService";
import { $SEASON_CLUSTER_ROUTE_QUERY_NAME } from "~/src/season-cluster/infrastructure/ui-services/seasonClusterUiService";

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: "/players/[playerId]-[playerName]",
    es: "/jugadores/[playerId]-[playerName]",
    it: "/giocatori/[playerId]-[playerName]",
    pt: "/jogadores/[playerId]-[playerName]",
    fr: "/joueurs/[playerId]-[playerName]",
    de: "/spieler/[playerId]-[playerName]",
  },
});

definePageMeta({
  middleware: ["not-found-king-of-kings-nations"],
  layout: "team-and-player",
});

const route = useRoute();
const router = useRouter();
const localePath = useLocalePath();
const { locale, t } = useI18n();
const { orderMatchesByDate } = matchService();
const {
  useFetchParamsPlayerSeasons,
  getPlayerSeasonStats,
  getPlayerSeasonMatches,
  findLastSeasonPlayedByPlayer,
  findLastSeasonPlayedByPlayerInRegion,
} = usePlayers();
const { getSeasonTeams, getSeason } = useSeasons();
const { getSeasonTeamPlayers, getSeasonTeamMatches } = useTeams();

const { currentSeason, region } = useCompetitionStore();
const teamAndPlayerStore = useTeamAndPlayerStore();
const { contextSiteTitle, contextDomain } = useWebContext();
const { findRegionInfoByCompetitionId } = RegionUiService();

const teamsObj = ref<Record<number, Team>>({});

const routePlayerId = route.params.playerId;
const player = ref<PlayerSeasons>();

//set player initial value and last season played or in play
const setPlayerValue = async (): Promise<void> => {
  if (!!teamAndPlayerStore.playerSeasonsPlayed && teamAndPlayerStore.playerId === Number(routePlayerId)) {
    player.value = teamAndPlayerStore.playerSeasonsPlayed;
  } else {
    const fetchOptsPlayerSeasons = useFetchParamsPlayerSeasons(Number(route.params.playerId));
    const { data, error } = await useFetch(fetchOptsPlayerSeasons);
    if (data.value) {
      player.value = data.value;
    }
    if (error.value && error.value.statusCode === 404) {
      throw createError({ statusCode: 404, statusMessage: "Player not found" });
    }
  }
};
await setPlayerValue();

const lastSeasonPlayedByPlayer: Season | undefined =
  player.value && player.value.seasons.length
    ? findLastSeasonPlayedByPlayer(player.value.seasons)
    : undefined;

const lastSeasonPlayedInRegion: Season | undefined =
  player.value && player.value.seasons.length
    ? findLastSeasonPlayedByPlayerInRegion(player.value.seasons, region.competitionId)
    : undefined;

if (
  !!lastSeasonPlayedByPlayer &&
  !!lastSeasonPlayedInRegion &&
  lastSeasonPlayedInRegion.id !== lastSeasonPlayedByPlayer.id
) {
  const currentRoute = route.path;
  await navigateTo(
    {
      path: currentRoute,
      query: {
        [$SEASON_CLUSTER_ROUTE_QUERY_NAME]: lastSeasonPlayedInRegion.seasonClusterId,
        [$SEASON_ROUTE_QUERY_NAME]: lastSeasonPlayedInRegion.id,
        [$REGION_ROUTE_QUERY_NAME]: region.id,
      },
    },
    { replace: true }
  );
}

const initialValues = lastSeasonPlayedByPlayer
  ? {
      seasonClusterId: lastSeasonPlayedByPlayer.seasonClusterId,
      seasonId: lastSeasonPlayedByPlayer.id,
      regionId: findRegionInfoByCompetitionId(lastSeasonPlayedByPlayer.competitionId, "id"),
    }
  : undefined;

const { seasonId, seasonClusterId, regionId } = useSeasonFilter(initialValues);

//get player global data for filters
const playerSeasonInfo = ref<SeasonPlayerInfo>();
const playerStats = ref<PlayerRanking[] | undefined>();
const playerSeasonMatches = ref<Match[] | undefined>([]);
const teamSeasonPlayers = ref<Player[]>();
const seasonMvpPartner = ref<SeasonStatsPartner>();
const loadingData = ref<boolean>(false);
const fullSeason = ref<Season | undefined>();
const featuredStatsKey = ref(0);
const seasonStatsKey = ref(0);
const teamId = ref<number>();
const regionChanged = ref<boolean | undefined>();

async function getPlayerData(playerId: number, firstLoad?: boolean): Promise<void> {
  try {
    loadingData.value = true;
    const seasonIdValue = seasonId.value;

    playerStats.value = seasonIdValue ? await getPlayerSeasonStats(seasonIdValue, playerId) : undefined;

    playerSeasonInfo.value = player.value?.seasons
      ? player.value?.seasons.find(season => season.season.id === seasonIdValue)
      : undefined;

    seasonMvpPartner.value =
      currentSeason && currentSeason.id === seasonIdValue
        ? currentSeason.metaInformation?.stats_partner
        : undefined;

    teamId.value = setPlayerTeam(undefined, "id");

    const teamIdValue = teamId.value;
    const playerPlayedCurrentSeason = !!playerSeasonInfo.value;

    if (!$SEASONS_WITHOUT_DATA.includes(Number(seasonIdValue)) && playerPlayedCurrentSeason) {
      playerSeasonMatches.value =
        seasonIdValue && playerId ? (await getPlayerSeasonMatches(seasonIdValue, playerId)) || [] : [];
      fullSeason.value =
        seasonIdValue === currentSeason.id
          ? currentSeason
          : seasonIdValue
            ? await getSeason(seasonIdValue)
            : undefined;
    } else {
      playerSeasonMatches.value =
        seasonIdValue && teamIdValue ? await getSeasonTeamMatches(seasonIdValue, teamIdValue) : [];
    }

    if (seasonIdValue) {
      const teams = await getSeasonTeams(seasonIdValue);
      teamsObj.value = await teamArrToObjById(teams);
    }

    if (!firstLoad) {
      await getRelatedPlayers();
    }
  } catch (e) {
    console.error(e);
  } finally {
    loadingData.value = false;
    seasonStatsKey.value++;
    featuredStatsKey.value++;
  }
}

const getRelatedPlayers = async () => {
  teamSeasonPlayers.value =
    seasonId.value && teamId.value ? await getSeasonTeamPlayers(seasonId.value, teamId.value) : undefined;
};

if (route.params.playerId) {
  await getPlayerData(Number(route.params.playerId), true);
}

//items set on mounted
const isMobile = ref(false);
const previousPageUrl = ref<string>();

onMounted(async () => {
  isMobile.value = window.innerWidth < 1200;
  window.addEventListener("resize", () => {
    isMobile.value = window.innerWidth < 1200;
  });

  const backUrl = router.options.history.state.back;
  if (typeof backUrl === "string") {
    previousPageUrl.value = backUrl;
  }

  await getRelatedPlayers();
});

//seo meta items
//TODO: Add players social media with 'sameAs' property
const schemaGender = player?.value?.gender
  ? `${player.value?.gender?.charAt(0).toUpperCase() + (player.value?.gender.slice(1) || "")}`
  : "";

const schemaPlayerImageUrl = player.value?.image?.url
  ? `https://${contextDomain}/_ipx/fit_outside&s_300x422/${setAliasUrl(player.value?.image?.url)}`
  : null;

useHead({
  script: () => [
    {
      type: "application/ld+json",
      innerHTML: `{"@context": "https://schema.org", "@type": "Person","name": "${player.value?.shortName}", "gender":"https://schema.org/${schemaGender}", "jobTitle":"${player.value?.role}", "birthDate":"${player.value?.birthDate}", "memberOf":"${player.value?.currentTeam?.name}", "image":"${schemaPlayerImageUrl}"}`,
    },
  ],
});

const playerName = player.value?.shortName;
const metaTitle = t("meta.title.player", { playerName });
const metaDescription = t("meta.description.player", { playerName, contextSiteTitle });

useSeoMeta({
  title: () => metaTitle,
  ogTitle: () => metaTitle,
  description: () => metaDescription,
  ogDescription: () => metaDescription,
  ogImage: () => schemaPlayerImageUrl,
});

//function to check for the correct current team of the player
watch(
  () => regionId,
  (newValue, oldValue) => {
    if (newValue !== oldValue) regionChanged.value = true;
    else if (newValue === oldValue) regionChanged.value = false;
  }
);

function setPlayerTeam(fallback?: string, key?: string, secondKey?: string): any | undefined {
  const checkAvailableKeys = value => {
    if (key && !secondKey) {
      return value[key];
    } else if (key && secondKey) {
      return value[key][secondKey];
    } else {
      return value;
    }
  };

  if (playerSeasonInfo.value) {
    if (
      playerSeasonInfo.value.season.isCurrent &&
      regionChanged.value === false &&
      player.value &&
      player.value.currentTeam
    ) {
      return checkAvailableKeys(player.value.currentTeam);
    } else if (playerSeasonInfo.value.teams.length) {
      if (playerSeasonInfo.value.teams.length > 1 && player.value && player.value.currentTeam) {
        const _currentTeam = playerSeasonInfo.value.teams.find(
          team => team.id === player.value?.currentTeam?.id
        );

        if (!!_currentTeam) {
          return checkAvailableKeys(_currentTeam);
        } else if (!_currentTeam) {
          return checkAvailableKeys(playerSeasonInfo.value.teams[0]);
        }
      } else {
        return checkAvailableKeys(playerSeasonInfo.value.teams[0]);
      }
    } else {
      return fallback ? fallback : undefined;
    }
  } else {
    if (player.value && player.value.currentTeam) {
      return checkAvailableKeys(player.value.currentTeam);
    } else {
      return fallback ? fallback : undefined;
    }
  }
}

//utils
const showSeasonsFilter = ref(false);

const toggleSeasonsFilter = () => {
  if (isMobile.value) {
    showSeasonsFilter.value = !showSeasonsFilter.value;
  }
};
</script>

<template>
  <div class="lg:px-6 relative kql-w-container py-6">
    <div v-if="playerSeasonInfo && player" class="px-4 py-2 lg:px-0 lg:py-0 lg:pt-6 text-gray-400 text-sm">
      <nuxt-link v-if="previousPageUrl" :to="previousPageUrl" no-prefetch>
        <prev-icon width="20" height="20" class="inline-block"></prev-icon>
        {{ $t("shared.back.button.title") }}
      </nuxt-link>

      <nuxt-link
        v-else-if="!loadingData && !previousPageUrl"
        :to="
          localePath({
            name: 'teams-team-teamName',
            params: {
              team: setPlayerTeam(undefined, 'id'),
              teamName: slugify(setPlayerTeam('', 'completeName')),
            },
          })
        "
      >
        <prev-icon width="20" height="20" class="inline-block"></prev-icon>
        {{ $t("shared.back.button.title") }}
      </nuxt-link>
    </div>

    <!--    Mobile Filter -->
    <div v-if="player" class="p-4 lg:p-0 border-y border-gray-800 lg:border-none">
      <button
        v-if="isMobile"
        class="text-gray-600 text-sm rounded-lg w-full flex justify-end items-center py-2"
        @click="toggleSeasonsFilter"
      >
        {{ $t("shared.filter.title") }}
        <!-- Arrow Filter -->
        <svg
          class="w-2.5 h-2.5 ms-1.5 text-gray-600"
          :class="showSeasonsFilter ? 'rotate-180' : ''"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 10 6"
        >
          <path
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="m1 1 4 4 4-4"
          />
        </svg>
      </button>

      <div v-show="showSeasonsFilter || !isMobile" class="mt-2 lg:my-6">
        <player-seasons-filter
          v-if="routePlayerId"
          v-model:seasonClusterId="seasonClusterId"
          v-model:seasonId="seasonId"
          v-model:regionId="regionId"
          :player-id="Number(routePlayerId)"
          :player-seasons="player.seasons"
          :initial-filter-values="initialValues"
          @change="getPlayerData(Number(routePlayerId))"
        ></player-seasons-filter>
      </div>
    </div>

    <hero-player-page
      v-if="player && playerSeasonInfo"
      :player-season-info="playerSeasonInfo"
      :team="setPlayerTeam(undefined)"
      :player="player"
      :region-id="regionId"
    ></hero-player-page>

    <base-backdrop-loading :active="loadingData" class="z-20"></base-backdrop-loading>

    <div class="flex flex-col lg:flex-row relative lg:gap-3 lg:justify-center lg:py-6 lg:pb-16 pb-12">
      <div class="order-last lg:order-none p-4 lg:p-0" v-if="!!player && !!playerSeasonMatches">
        <div class="mx-6 mb-4 lg:mb-2 lg:mx-0">
          <h2 class="kql-page-section-title">
            {{ $t("player.team.matches.title") }}
          </h2>
          <hr />
        </div>
        <player-season-matches
          :playerSeasonMatches="orderMatchesByDate(playerSeasonMatches as Match[])"
          :teams="teamsObj"
          :player-image="
            playerSeasonInfo && playerSeasonInfo.season.id === currentSeason?.id
              ? player?.image
              : playerSeasonInfo?.playerImage
                ? playerSeasonInfo?.playerImage
                : player?.image
          "
          :player-id="player?.id || Number(routePlayerId)"
          :season-turns="fullSeason"
          :season-id="seasonId"
          :team-info="setPlayerTeam(undefined)"
          :mvp-partner="seasonMvpPartner"
        ></player-season-matches>
      </div>

      <div class="w-full p-4 lg:p-0">
        <div class="mx-6 mb-4 lg:mb-2 lg:mx-0">
          <h2 class="kql-page-section-title">
            {{ $t("player.team.season.stats.title") }}
          </h2>
          <hr />
        </div>
        <featured-player-stats
          v-if="!!player && playerStats"
          :stats="playerStats"
          :stats-partner="seasonMvpPartner"
          :player-role="player.role"
          :season-id="seasonId"
          :key="featuredStatsKey"
        ></featured-player-stats>
        <div class="mt-6 lg:mt-1">
          <player-stats
            v-if="!!player && playerStats"
            :stats="playerStats"
            :player-role="player.role"
            :season-id="seasonId"
            :key="seasonStatsKey"
          ></player-stats>
        </div>
      </div>
    </div>

    <div :class="isMobile ? 'min-h-[960px]' : 'min-h-[325px]'">
      <team-season-players-slider
        v-if="player && !!teamSeasonPlayers && seasonId"
        :team="setPlayerTeam(undefined)"
        :player="player"
        :team-season-players="teamSeasonPlayers"
        :season-id="seasonId"
        :player-season-info="playerSeasonInfo"
        :is-mobile="isMobile"
        :season-mvp-partner="seasonMvpPartner"
      ></team-season-players-slider>
    </div>
    <base-nuxt-link
      :to="localePath({ name: 'region-PlayersDraft', params: { region: region?.slug } })"
      text-size="text-xs"
      primary
      class="block w-fit mx-auto my-4 mb-8"
      >{{ $t("player.view.all.draftPlayers") }}</base-nuxt-link
    >
  </div>
</template>

<style scoped></style>
